import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  PlusCircle, 
  MinusCircle, 
  Calculator, 
  Clock,
  User,
  Calendar,
  TrendingUp,
  TrendingDown,
  Receipt,
  Lock,
  Unlock,
  FileText,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  CreditCard
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

const CashierPage = () => {
  const { company, person } = useAuth();
  const [currentRegister, setCurrentRegister] = useState(null);
  const [movements, setMovements] = useState([]);
  const [sales, setSales] = useState([]);
  const [activeTab, setActiveTab] = useState('movements'); // 'movements' ou 'sales'
  const [loading, setLoading] = useState(true);
  const [showOpenDialog, setShowOpenDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showMovementDialog, setShowMovementDialog] = useState(false);
  const [openingBalance, setOpeningBalance] = useState('');
  const [closingBalance, setClosingBalance] = useState('');
  const [movementData, setMovementData] = useState({
    type: 'entry',
    amount: '',
    description: '',
    category: '',
    receipt_number: ''
  });

  useEffect(() => {
    if (company?.id) {
      loadCashRegister();
    }
  }, [company]);

  const loadCashRegister = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading cash register for company:', company.id);
      
      // Buscar caixa atual
      const register = await dataHelpers.getCurrentCashRegister(company.id);
      console.log('🏪 Current cash register loaded:', register);
      setCurrentRegister(register);
      
      if (register) {
        // Carregar movimentações do dia
        console.log('🔄 Loading cash movements for register:', register.id);
        const movements = await dataHelpers.getCashMovements(register.id);
        console.log('💰 Cash movements loaded:', movements);
        setMovements(movements || []);
        
        // Carregar vendas do dia
        try {
          const salesData = await dataHelpers.getSales(company.id, {
            startDate: new Date().toISOString().split('T')[0] + 'T00:00:00',
            endDate: new Date().toISOString().split('T')[0] + 'T23:59:59'
          });
          console.log('📊 Sales for today:', salesData);
          setSales(salesData || []);
        } catch (salesError) {
          console.error('❌ Error loading sales:', salesError);
          setSales([]);
        }
      }
    } catch (error) {
      console.error('❌ Error loading cash register:', error);
      toast.error('Erro ao carregar dados do caixa');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCashRegister = async () => {
    if (!openingBalance || parseFloat(openingBalance) < 0) {
      toast.error('Informe um valor válido para o saldo inicial');
      return;
    }

    try {
      const registerData = {
        company_id: company.id,
        opened_by: person.auth_user_id,
        opening_balance: parseFloat(openingBalance),
        status: 'open',
        // Inicializar contadores de vendas
        total_sales: 0,
        cash_sales: 0,
        card_sales: 0,
        pix_sales: 0,
        balance_sales: 0,
        sales_count: 0,
        cash_sales_count: 0,
        card_sales_count: 0,
        pix_sales_count: 0,
        balance_sales_count: 0
      };

      const newRegister = await dataHelpers.createCashRegister(registerData);
      setCurrentRegister(newRegister);
      setOpeningBalance('');
      setShowOpenDialog(false);
      toast.success('Caixa aberto com sucesso!');
    } catch (error) {
      console.error('Error opening cash register:', error);
      toast.error('Erro ao abrir caixa');
    }
  };

  const handleCloseCashRegister = async () => {
    if (!closingBalance || parseFloat(closingBalance) < 0) {
      toast.error('Informe um valor válido para o saldo final');
      return;
    }

    try {
      const updateData = {
        closed_by: person.auth_user_id,
        closed_at: new Date().toISOString(),
        closing_balance: parseFloat(closingBalance),
        status: 'closed'
      };

      // Calcular saldo do sistema
      const systemBalance = await dataHelpers.calculateSystemBalance(currentRegister.id);
      updateData.system_balance = systemBalance;
      updateData.difference = parseFloat(closingBalance) - systemBalance;

      await dataHelpers.updateCashRegister(currentRegister.id, updateData);
      setCurrentRegister(null);
      setClosingBalance('');
      setShowCloseDialog(false);
      toast.success('Caixa fechado com sucesso!');
    } catch (error) {
      console.error('Error closing cash register:', error);
      toast.error('Erro ao fechar caixa');
    }
  };

  const handleAddMovement = async () => {
    if (!movementData.amount || !movementData.description) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      const movement = {
        ...movementData,
        company_id: company.id,
        cash_register_id: currentRegister.id,
        auth_user_id: person.auth_user_id,
        amount: parseFloat(movementData.amount)
      };

      const newMovement = await dataHelpers.createCashMovement(movement);
      setMovements(prev => [newMovement, ...prev]);
      setMovementData({
        type: 'entry',
        amount: '',
        description: '',
        category: '',
        receipt_number: ''
      });
      setShowMovementDialog(false);
      toast.success('Movimentação registrada com sucesso!');
    } catch (error) {
      console.error('Error adding movement:', error);
      toast.error('Erro ao registrar movimentação');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const calculateTotals = () => {
    const entries = movements
      .filter(m => m.type === 'entry')
      .reduce((sum, m) => sum + parseFloat(m.amount), 0);
    
    const withdrawals = movements
      .filter(m => m.type === 'withdrawal')
      .reduce((sum, m) => sum + parseFloat(m.amount), 0);

    return { entries, withdrawals };
  };

  const { entries, withdrawals } = calculateTotals();
  
  // Calcular vendas em dinheiro do dia atual
  const cashSalesTotal = sales
    .filter(sale => sale.payment_method === 'cash')
    .reduce((sum, sale) => sum + parseFloat(sale.total_amount || 0), 0);
  
  const currentBalance = currentRegister ? 
    parseFloat(currentRegister.opening_balance) + entries - withdrawals + cashSalesTotal : 0;

  if (loading) {
    return (
      
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="large" />
        </div>
      
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Controle de Caixa</h1>
            <p className="text-gray-600">
              {currentRegister ? 'Caixa aberto - gerencie as movimentações do dia' : 'Nenhum caixa aberto hoje'}
            </p>
          </div>
          <div className="mt-4 sm:mt-0 space-x-2">
            {!currentRegister ? (
              <Button 
                onClick={() => setShowOpenDialog(true)}
                className="bg-green-600 hover:bg-green-700"
              >
                <Unlock className="w-4 h-4 mr-2" />
                Abrir Caixa
              </Button>
            ) : (
              <>
                <Button 
                  variant="outline"
                  onClick={() => setShowMovementDialog(true)}
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Nova Movimentação
                </Button>
                <Button 
                  onClick={() => setShowCloseDialog(true)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Fechar Caixa
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Status Cards */}
        {currentRegister && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              <Card className="p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-blue-100">
                    <DollarSign className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Saldo Inicial</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatCurrency(currentRegister.opening_balance)}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-green-100">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Entradas</p>
                    <p className="text-2xl font-semibold text-green-600">
                      {formatCurrency(entries)}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-red-100">
                    <TrendingDown className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Saídas</p>
                    <p className="text-2xl font-semibold text-red-600">
                      {formatCurrency(withdrawals)}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-yellow-100">
                    <Receipt className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Vendas em Dinheiro</p>
                    <p className="text-2xl font-semibold text-yellow-600">
                      {formatCurrency(cashSalesTotal)}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-purple-100">
                    <Calculator className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Saldo Atual</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatCurrency(currentBalance)}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Sales Statistics */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Estatísticas de Vendas do Caixa</h3>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <Receipt className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Total Vendas</p>
                  <p className="text-xl font-bold text-blue-600">
                    {formatCurrency(currentRegister.total_sales || 0)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {currentRegister.sales_count || 0} vendas
                  </p>
                </div>

                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Dinheiro</p>
                  <p className="text-xl font-bold text-green-600">
                    {formatCurrency(currentRegister.cash_sales || 0)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {currentRegister.cash_sales_count || 0} vendas
                  </p>
                </div>

                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <CreditCard className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Cartão</p>
                  <p className="text-xl font-bold text-purple-600">
                    {formatCurrency(currentRegister.card_sales || 0)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {currentRegister.card_sales_count || 0} vendas
                  </p>
                </div>

                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <svg className="w-8 h-8 text-orange-600 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                  </svg>
                  <p className="text-sm text-gray-600">PIX</p>
                  <p className="text-xl font-bold text-orange-600">
                    {formatCurrency(currentRegister.pix_sales || 0)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {currentRegister.pix_sales_count || 0} vendas
                  </p>
                </div>

                <div className="text-center p-4 bg-indigo-50 rounded-lg">
                  <User className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Saldo Aluno</p>
                  <p className="text-xl font-bold text-indigo-600">
                    {formatCurrency(currentRegister.balance_sales || 0)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {currentRegister.balance_sales_count || 0} vendas
                  </p>
                </div>
              </div>
            </Card>
          </>
        )}

        {/* Cash Register Info */}
        {currentRegister && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações do Caixa</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Abertura</p>
                  <p className="text-gray-900">{formatDateTime(currentRegister.opened_at)}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Aberto por</p>
                  <p className="text-gray-900">{person.name}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Status</p>
                  <p className="text-green-600 font-medium">Aberto</p>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Movements and Sales List */}
        {currentRegister && (
          <Card>
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Atividades do Dia</h3>
                <div className="flex space-x-2 bg-gradient-to-r from-gray-100 to-gray-200 p-1.5 rounded-xl shadow-inner">
                  <button
                    onClick={() => setActiveTab('movements')}
                    className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 flex items-center space-x-2 ${
                      activeTab === 'movements'
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg transform scale-105'
                        : 'text-gray-700 hover:text-blue-600 hover:bg-white/50'
                    }`}
                  >
                    <TrendingUp className="w-4 h-4" />
                    <span>Movimentações</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      activeTab === 'movements' 
                        ? 'bg-white/20 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {movements.length}
                    </span>
                  </button>
                  <button
                    onClick={() => setActiveTab('sales')}
                    className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 flex items-center space-x-2 ${
                      activeTab === 'sales'
                        ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg transform scale-105'
                        : 'text-gray-700 hover:text-green-600 hover:bg-white/50'
                    }`}
                  >
                    <Receipt className="w-4 h-4" />
                    <span>Vendas</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      activeTab === 'sales' 
                        ? 'bg-white/20 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {sales.length}
                    </span>
                  </button>
                </div>
              </div>
            </div>
            
            <div className="overflow-hidden">
              {activeTab === 'movements' ? (
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Tipo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Valor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Descrição
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Horário
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {movements.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-12 text-center">
                          <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500">Nenhuma movimentação manual registrada hoje</p>
                          <p className="text-xs text-gray-400 mt-1">
                            Use "Nova Movimentação" para registrar entradas/saídas de dinheiro
                          </p>
                        </td>
                      </tr>
                    ) : (
                      movements.map((movement) => (
                        <tr key={movement.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              movement.type === 'entry' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {movement.type === 'entry' ? (
                                <TrendingUp className="w-3 h-3 mr-1" />
                              ) : (
                                <TrendingDown className="w-3 h-3 mr-1" />
                              )}
                              {movement.type === 'entry' ? 'Entrada' : 'Saída'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`font-medium ${
                              movement.type === 'entry' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {formatCurrency(movement.amount)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {movement.description}
                              </p>
                              {movement.category && (
                                <p className="text-xs text-gray-500">
                                  Categoria: {movement.category}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {formatDateTime(movement.created_at)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              ) : (
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Valor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Pagamento
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Horário
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sales.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-12 text-center">
                          <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500">Nenhuma venda registrada hoje</p>
                          <p className="text-xs text-gray-400 mt-1">
                            As vendas do PDV aparecerão aqui automaticamente
                          </p>
                        </td>
                      </tr>
                    ) : (
                      sales.map((sale) => {
                        const paymentMethodLabels = {
                          'cash': 'Dinheiro',
                          'card': 'Cartão',
                          'pix': 'PIX',
                          'student_balance': 'Saldo Aluno'
                        };
                        
                        const paymentMethodColors = {
                          'cash': 'bg-green-100 text-green-800',
                          'card': 'bg-purple-100 text-purple-800',
                          'pix': 'bg-orange-100 text-orange-800',
                          'student_balance': 'bg-blue-100 text-blue-800'
                        };

                        return (
                          <tr key={sale.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {sale.students?.name || 'Consumidor Final'}
                                </p>
                                {sale.students && (
                                  <p className="text-xs text-gray-500">
                                    {sale.students.registration_number} • {sale.students.class_name}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="font-medium text-green-600">
                                {formatCurrency(sale.total_amount)}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                paymentMethodColors[sale.payment_method] || 'bg-gray-100 text-gray-800'
                              }`}>
                                {paymentMethodLabels[sale.payment_method] || sale.payment_method}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {formatDateTime(sale.created_at)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
        )}

        {/* No register message */}
        {!currentRegister && (
          <Card className="p-12 text-center">
            <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Caixa Fechado</h3>
            <p className="text-gray-500 mb-6">
              Para começar a trabalhar, abra um novo caixa informando o valor inicial em dinheiro.
            </p>
            <Button 
              onClick={() => setShowOpenDialog(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              <Unlock className="w-4 h-4 mr-2" />
              Abrir Caixa
            </Button>
          </Card>
        )}
      </div>

      {/* Open Dialog */}
      {showOpenDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Abrir Caixa</h3>
            <div className="space-y-4">
              <Input
                label="Valor inicial em dinheiro"
                type="number"
                step="0.01"
                min="0"
                value={openingBalance}
                onChange={(e) => setOpeningBalance(e.target.value)}
                placeholder="0,00"
              />
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <AlertCircle className="w-4 h-4" />
                <span>Informe o valor em dinheiro disponível no caixa</span>
              </div>
            </div>
            <div className="mt-6 flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowOpenDialog(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleOpenCashRegister}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                Abrir Caixa
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Close Dialog */}
      {showCloseDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Fechar Caixa</h3>
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Saldo calculado pelo sistema:</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatCurrency(currentBalance)}
                </p>
              </div>
              <Input
                label="Valor real em dinheiro"
                type="number"
                step="0.01"
                min="0"
                value={closingBalance}
                onChange={(e) => setClosingBalance(e.target.value)}
                placeholder="0,00"
              />
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <AlertCircle className="w-4 h-4" />
                <span>Conte o dinheiro físico disponível no caixa</span>
              </div>
            </div>
            <div className="mt-6 flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowCloseDialog(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCloseCashRegister}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Fechar Caixa
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Movement Dialog */}
      {showMovementDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Nova Movimentação</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo
                </label>
                <select
                  value={movementData.type}
                  onChange={(e) => setMovementData(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="entry">Entrada</option>
                  <option value="withdrawal">Saída</option>
                </select>
              </div>
              
              <Input
                label="Valor"
                type="number"
                step="0.01"
                min="0"
                value={movementData.amount}
                onChange={(e) => setMovementData(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0,00"
                required
              />
              
              <Input
                label="Descrição"
                value={movementData.description}
                onChange={(e) => setMovementData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ex: Troco para vendas"
                required
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria
                </label>
                <select
                  value={movementData.category}
                  onChange={(e) => setMovementData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Selecione uma categoria</option>
                  <option value="change">Troco</option>
                  <option value="expense">Despesa</option>
                  <option value="deposit">Depósito</option>
                  <option value="other">Outros</option>
                </select>
              </div>
              
              <Input
                label="Número do comprovante (opcional)"
                value={movementData.receipt_number}
                onChange={(e) => setMovementData(prev => ({ ...prev, receipt_number: e.target.value }))}
                placeholder="Ex: 12345"
              />
            </div>
            <div className="mt-6 flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowMovementDialog(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAddMovement}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Registrar
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CashierPage;
